<script lang="ts">
  import type { PriceData } from "./+page.svelte";

  let no4: SVGElement | undefined = $state();
  let no3: SVGElement | undefined = $state();
  let no1: SVGElement | undefined = $state();
  let no5: SVGElement | undefined = $state();
  let no2: SVGElement | undefined = $state();

  let no4c: SVGElement | undefined = $state();
  let no3c: SVGElement | undefined = $state();
  let no1c: SVGElement | undefined = $state();
  let no5c: SVGElement | undefined = $state();
  let no2c: SVGElement | undefined = $state();

  interface Props {
    selectedArea: string;
    priceData: PriceData;
    width: number;
    height: number;
  }

  let { selectedArea = $bindable(""), priceData, width, height }: Props = $props();

  const getPrice = (area: string) => {
    const selectedAreaData = priceData?.price_areas.find((a) => a.price_area === area)?.periods;
    return selectedAreaData
      ? selectedAreaData.reduce((acc, curr) => curr.value + acc, 0) / selectedAreaData.length
      : 0;
  };

  // Roving tabindex helpers for keyboard arrow navigation
  const order = ["NO4", "NO3", "NO1", "NO5", "NO2"] as const;
  type AreaId = (typeof order)[number];

  // Map area IDs to their element references for cleaner lookup
  const areaElements = $derived({
    NO4: no4,
    NO3: no3,
    NO1: no1,
    NO5: no5,
    NO2: no2,
  });

  const getEl = (id: AreaId): SVGElement | undefined => areaElements[id];

  // Navigate to adjacent area without changing selection
  const navigateToArea = (direction: 1 | -1, currentId: AreaId) => {
    const currentIndex = order.indexOf(currentId);
    const nextIndex = (currentIndex + direction + order.length) % order.length;
    const nextId = order[nextIndex];

    focusedArea = nextId;
    queueMicrotask(() => getEl(nextId)?.focus());
  };

  // For roving tabindex, we need to track which region currently has focus
  let focusedArea: AreaId = $state(order[0]);

  // Only the focused region should be tabbable (roving tabindex pattern)
  const getTabIndex = (id: AreaId): 0 | -1 => (focusedArea === id ? 0 : -1);

  // Activate the focused region (change selection)
  const activateArea = (id: AreaId, element: SVGGElement) => {
    selectedArea = id;
    focusedArea = id;
    // Maintain focus for continued keyboard navigation
    queueMicrotask(() => element.focus());
  };

  // Handle keyboard interactions following ARIA authoring practices
  const handleKeyboardNavigation = (e: KeyboardEvent, id: AreaId) => {
    const element = e.currentTarget as SVGGElement;

    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        activateArea(id, element);
        break;

      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        navigateToArea(1, id);
        break;

      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        navigateToArea(-1, id);
        break;

      case "Home":
        e.preventDefault();
        focusedArea = order[0];
        queueMicrotask(() => getEl(order[0])?.focus());
        break;

      case "End":
        e.preventDefault();
        {
          const lastArea = order[order.length - 1];
          focusedArea = lastArea;
          queueMicrotask(() => getEl(lastArea)?.focus());
        }
        break;
    }
  };

  // Handle mouse/touch interactions
  const handleAreaClick = (e: MouseEvent, id: AreaId) => {
    const element = e.currentTarget as SVGGElement;
    activateArea(id, element);
  };

  // Sync focus state when selection changes externally (from parent component)
  $effect(() => {
    if (selectedArea && (order as readonly string[]).includes(selectedArea)) {
      focusedArea = selectedArea as AreaId;
    }
  });
</script>

<svg
  role="img"
  aria-labelledby="mapTitle mapDesc"
  {width}
  {height}
  viewBox="-350 0 1900 2200"
  preserveAspectRatio="xMinYMid meet"
  xmlns="http://www.w3.org/2000/svg"
>
  <title id="mapTitle">Interaktivt kart over strømpriser i Norge - Prisområder NO1-NO5</title>
  <desc id="mapDesc">
    Interaktivt kart som viser strømpriser for alle norske prisområder (NO1, NO2, NO3, NO4, NO5).
    Klikk eller naviger med tastatur for å se gjennomsnittlig strømpris siste 12 måneder per kWh.
    Sammenlign strømpriser mellom Sørøst-Norge (NO1), Sørvest-Norge (NO2), Midt-Norge (NO3),
    Nord-Norge (NO4) og Vest-Norge (NO5).
  </desc>

  <g role="tablist" aria-label="Norske strømprisområder" aria-orientation="horizontal">
    <!-- NO4 -->
    <g
      id="NO4"
      role="tab"
      aria-selected={selectedArea === "NO4"}
      aria-controls="tabpanel-NO4"
      class="fill-$primary-strong"
      class:selected={selectedArea === "NO4"}
      bind:this={no4}
      onclick={(e) => handleAreaClick(e, "NO4")}
      onkeydown={(e) => handleKeyboardNavigation(e, "NO4")}
      onmouseenter={(e) => {
        e.preventDefault();
        no4?.classList.add("hover");
        no4c?.classList.add("c-hover");
      }}
      onmouseleave={(e) => {
        e.preventDefault();
        no4?.classList.remove("hover");
        no4c?.classList.remove("c-hover");
      }}
      tabindex={getTabIndex("NO4")}
    >
      <g>
        <path
          d="M1493 254.184L1360.91 362.156L1390.93 287.925L1322.89 227.191L1278.86 211.445L1232.83 220.443L1208.81 254.184L1202.81 391.399H1178.79L1152.77 440.886L1096.73 418.392H1076.72L1020.68 429.639C1014.68 423.64 999.469 403.995 986.661 373.403C973.852 342.811 969.316 333.664 968.649 332.914L956.641 357.657H930.623V440.886L908.609 470.128L816.547 458.881V535.361L752.504 519.615L710.476 602.844V661.329L678.454 791.795L628.421 812.04L612.41 926.76L588.394 980.746L468.314 926.76L538.361 740.058L606.406 643.333L616.413 571.352L656.44 515.117L722.484 458.881L646.433 470.128L606.406 497.121V458.881L628.421 440.886L664.445 429.639L682.457 402.646L688.461 350.909L704.472 341.911L710.476 382.401H716.48L726.487 341.911H738.495V382.401L776.52 366.655V332.914L806.54 323.916L812.544 276.678L848.568 258.683L862.578 287.925L868.582 281.177L854.572 240.688L874.586 220.443L898.602 215.944L918.615 179.953H930.623V240.688L938.629 211.445L948.635 186.702L962.645 173.205L986.661 195.699H1002.67V186.702L956.641 143.963L986.661 123.718H1034.69L1040.7 119.219L1084.73 67.4825L1110.74 92.2261L1120.75 85.4779L1130.76 38.2401L1170.78 29.2424L1182.79 6.74825L1204.81 0H1222.82L1166.78 148.462L1182.79 155.21L1230.82 38.2401L1244.83 29.2424L1238.83 101.224H1250.84L1294.87 6.74825L1360.91 17.9953L1316.88 123.718H1334.89L1374.92 51.7366L1493 148.462L1446.97 191.2L1360.91 173.205V186.702L1493 254.184Z"
        />
        <path
          d="M1006.67 92.2261L1000.67 112.471H1002.67L1010.68 105.723L1036.69 101.224L1064.71 65.2331L1068.72 53.986L1062.71 42.7389L1042.7 56.2354L1050.7 62.9837V69.7319H1042.7L1040.7 62.9837H1032.69L1026.69 76.4802L1016.68 67.4825L1006.67 76.4802L994.666 80.979V92.2261H1006.67Z"
        />
        <path
          d="M842.564 195.699L860.576 211.445H862.578H868.582L898.602 186.702L892.598 179.953L878.589 188.951L876.587 175.455L872.584 186.702L860.576 166.457L842.564 179.953V195.699Z"
        />
        <path
          d="M854.572 220.443L836.56 204.697L838.562 215.944V227.191L832.558 229.441V222.692H818.548V229.441H824.552V236.189H812.544L808.542 249.685L802.538 254.184L812.544 265.431L818.548 260.932L830.556 254.184L836.56 258.683L842.564 256.434L838.562 249.685V238.438L854.572 224.942V220.443Z"
        />
        <path
          d="M750.503 330.664L756.507 341.911H760.509V332.914L768.515 321.667L778.521 317.168H794.532L800.536 312.669L794.532 296.923L804.539 290.175L806.54 281.177L800.536 269.93L794.532 258.683L786.527 254.184H776.52L770.516 265.431H760.509L766.513 276.678H752.504L744.499 274.429L752.504 287.925L746.5 296.923L752.504 308.17H738.495L744.499 312.669L738.495 323.916L750.503 330.664Z"
        />
        <path
          d="M714.479 301.422L710.476 290.175L700.469 301.422L692.464 314.918L684.458 319.417L680.456 335.163L676.453 346.41L682.457 350.909V341.911L690.462 339.662L700.469 330.664L706.473 312.669L714.479 301.422Z"
        />
        <path
          d="M672.45 364.406L660.442 341.911L654.438 346.41L656.44 357.657L662.444 364.406V371.154H654.438L648.434 382.401L642.43 380.152L634.425 375.653L628.421 382.401L624.418 398.147L628.421 407.145L636.426 398.147L638.428 389.149L642.43 393.648H650.436L656.44 389.149L658.441 380.152L664.445 382.401L660.442 395.897L648.434 400.396L646.433 407.145L662.444 404.895L674.452 395.897V386.9L670.449 382.401V375.653L676.453 373.403L678.454 371.154L672.45 364.406Z"
        />
        <path
          d="M594.398 488.124L580.389 465.629H576.386L574.385 481.375L558.374 488.124L540.362 506.119L532.357 528.613L526.353 542.109L532.357 546.608L544.365 537.611L558.374 519.615L574.385 517.366L588.394 503.869L594.398 488.124Z"
        />
      </g>
      <path stroke-width="8" stroke-linecap="round" stroke="#ffd9c4" d="M650 700H900"></path>
      <circle
        cx="650"
        cy="700"
        r="25"
        stroke-width="8"
        stroke-linecap="round"
        stroke="#ffd9c4"
        fill="#ffd9c4"
      />
      <circle
        cx="1070"
        cy="700"
        r="175"
        stroke-width="8"
        stroke-linecap="round"
        stroke="#ffd9c4"
        fill="none"
        class:c-selected={selectedArea === "NO4"}
        bind:this={no4c}
      />
      <text id="no4-info" x="1070" y="650" text-anchor="middle" fill="#002987">NO4</text>
      <text class="bold" x="1070" y="720" text-anchor="middle" fill="#002987"
        >{getPrice("NO4").toFixed(2).replace(".", ",")}
      </text>
      <text x="1070" y="790" text-anchor="middle" fill="#002987">øre/kWh</text>
    </g>
    <!-- NO3 -->
    <g
      id="NO3"
      role="tab"
      aria-selected={selectedArea === "NO3"}
      aria-controls="tabpanel-NO3"
      class="fill-$primary-strong"
      class:selected={selectedArea === "NO3"}
      bind:this={no3}
      onmouseenter={(e) => {
        e.preventDefault();
        no3?.classList.add("hover");
        no3c?.classList.add("c-hover");
      }}
      onmouseleave={(e) => {
        e.preventDefault();
        no3?.classList.remove("hover");
        no3c?.classList.remove("c-hover");
      }}
      tabindex={getTabIndex("NO3")}
      onclick={(e) => handleAreaClick(e, "NO3")}
      onkeydown={(e) => handleKeyboardNavigation(e, "NO3")}
    >
      <g>
        <path
          d="M580.389 996.492L458.307 942.506H456.306L420.281 976.247L430.288 1023.48V1032.48H406.272L352.236 1099.96H336.225H324.217L320.214 1104.46L330.221 1126.96L316.212 1140.45L254.17 1174.2L190.127 1214.69L112.075 1284.42L66.0442 1329.41L58.0389 1349.65L228.153 1376.64L502.336 1320.41L488.327 1183.19L510.342 1111.21L538.361 1088.72L580.389 1104.46L600.402 1045.98L580.389 996.492Z"
        />
        <path
          d="M306.205 1126.96L302.202 1111.21H298.2H292.196V1120.21L278.186 1126.96H268.18L254.17 1135.96V1151.7H268.18L278.186 1140.45H298.2L306.205 1126.96Z"
        />
        <path
          d="M288.193 1104.46L278.186 1093.22L274.184 1099.96L268.18 1104.46L256.172 1111.21V1117.96H268.18L282.189 1111.21L288.193 1104.46Z"
        />
        <path
          d="M246.165 1153.95L240.161 1147.2L234.157 1151.7H222.149V1158.45L234.157 1165.2H246.165V1153.95Z"
        />
      </g>
      <path stroke-width="8" stroke-linecap="round" stroke="#ffd9c4" d="M400 1200L100 1000"></path>
      <circle
        cx="400"
        cy="1200"
        r="25"
        stroke-width="8"
        stroke-linecap="round"
        stroke="#ffd9c4"
        fill="#ffd9c4"
      />
      <circle
        cx="-50"
        cy="900"
        r="175"
        stroke-width="8"
        stroke-linecap="round"
        stroke="#ffd9c4"
        fill="none"
        class:c-selected={selectedArea === "NO3"}
        bind:this={no3c}
      />
      <text id="no3-info" x="-50" y="850" text-anchor="middle" fill="#002987">NO3</text>
      <text class="bold" x="-50" y="920" text-anchor="middle" fill="#002987"
        >{getPrice("NO3").toFixed(2).replace(".", ",")}
      </text>
      <text x="-50" y="990" text-anchor="middle" fill="#002987">øre/kWh</text>
    </g>

    <!-- NO1 -->
    <g
      id="NO1"
      role="tab"
      aria-selected={selectedArea === "NO1"}
      aria-controls="tabpanel-NO1"
      class="fill-$primary-strong"
      class:selected={selectedArea === "NO1"}
      bind:this={no1}
      onmouseenter={(e) => {
        e.preventDefault();
        no1?.classList.add("hover");
        no1c?.classList.add("c-hover");
      }}
      onmouseleave={(e) => {
        e.preventDefault();
        no1?.classList.remove("hover");
        no1c?.classList.remove("c-hover");
      }}
      tabindex={getTabIndex("NO1")}
      onclick={(e) => handleAreaClick(e, "NO1")}
      onkeydown={(e) => handleKeyboardNavigation(e, "NO1")}
    >
      <g>
        <path
          d="M292.196 1660.07L234.157 1394.64L502.336 1338.4L510.342 1376.64L548.367 1417.13V1459.87L510.342 1471.12L524.351 1558.85V1581.34L476.319 1675.82L472.316 1763.54L468.314 1799.53L464.311 1815.28H456.306L446.299 1804.03V1779.29L402.269 1745.55V1646.57L388.26 1653.32L380.255 1734.3L348.233 1759.04L244.164 1696.06L292.196 1660.07Z"
        />
      </g>
      <path stroke-width="8" stroke-linecap="round" stroke="#ffd9c4" d="M600 1600H400"></path>
      <circle
        cx="400"
        cy="1600"
        r="25"
        stroke-width="8"
        stroke-linecap="round"
        stroke="#ffd9c4"
        fill="#ffd9c4"
      />
      <circle
        cx="775"
        cy="1600"
        r="175"
        stroke-width="8"
        stroke-linecap="round"
        stroke="#ffd9c4"
        fill="none"
        class:c-selected={selectedArea === "NO1"}
        bind:this={no1c}
      />
      <text id="no1-info" x="775" y="1550" text-anchor="middle" fill="#002987">NO1</text>
      <text class="bold" x="775" y="1620" text-anchor="middle" fill="#002987"
        >{getPrice("NO1").toFixed(2).replace(".", ",")}
      </text>
      <text x="775" y="1690" text-anchor="middle" fill="#002987">øre/kWh</text>
    </g>

    <!-- NO5 -->
    <g
      id="NO5"
      role="tab"
      aria-selected={selectedArea === "NO5"}
      aria-controls="tabpanel-NO5"
      class="fill-$primary-strong"
      class:selected={selectedArea === "NO5"}
      bind:this={no5}
      onmouseenter={(e) => {
        e.preventDefault();
        no5?.classList.add("hover");
        no5c?.classList.add("c-hover");
      }}
      onmouseleave={(e) => {
        e.preventDefault();
        no5?.classList.remove("hover");
        no5c?.classList.remove("c-hover");
      }}
      tabindex={getTabIndex("NO5")}
      onclick={(e) => handleAreaClick(e, "NO5")}
      onkeydown={(e) => handleKeyboardNavigation(e, "NO5")}
    >
      <g>
        <path
          d="M218.146 1394.64L52.0349 1365.4L0 1507.11L10.0067 1646.57L14.0094 1655.57L88.059 1610.58H100.067L228.153 1682.56L274.184 1653.32L218.146 1394.64Z"
        />
      </g>
      <path stroke-width="8" stroke-linecap="round" stroke="#ffd9c4" d="M100 1500L-100 1300"></path>
      <circle
        cx="100"
        cy="1500"
        r="25"
        stroke-width="8"
        stroke-linecap="round"
        stroke="#ffd9c4"
        fill="#ffd9c4"
      />
      <circle
        cx="-140"
        cy="1265"
        r="175"
        stroke-width="8"
        stroke-linecap="round"
        stroke="#ffd9c4"
        fill="none"
        class:c-selected={selectedArea === "NO5"}
        bind:this={no5c}
      />
      <text id="no5-info" x="-140" y="1215" text-anchor="middle" fill="#002987">NO5</text>
      <text class="bold" x="-140" y="1285" text-anchor="middle" fill="#002987"
        >{getPrice("NO5").toFixed(2).replace(".", ",")}
      </text>
      <text x="-140" y="1355" text-anchor="middle" fill="#002987">øre/kWh</text>
    </g>

    <!-- NO2 -->
    <g
      id="NO2"
      role="tab"
      aria-selected={selectedArea === "NO2"}
      aria-controls="tabpanel-NO2"
      class="fill-$primary-strong"
      class:selected={selectedArea === "NO2"}
      bind:this={no2}
      onmouseenter={(e) => {
        e.preventDefault();
        no2?.classList.add("hover");
        no2c?.classList.add("c-hover");
      }}
      onmouseleave={(e) => {
        e.preventDefault();
        no2?.classList.remove("hover");
        no2c?.classList.remove("c-hover");
      }}
      tabindex={getTabIndex("NO2")}
      onclick={(e) => handleAreaClick(e, "NO2")}
      onkeydown={(e) => handleKeyboardNavigation(e, "NO2")}
    >
      <g>
        <path
          d="M14.0094 1828.78V1675.82L90.0603 1630.83H100.067L336.225 1774.79L300.201 1837.77L198.133 1925.5L190.127 1930L108.072 1923.25L14.0094 1828.78Z"
        />
      </g>

      <path stroke-width="8" stroke-linecap="round" stroke="#ffd9c4" d="M150 1800 L400 1900"></path>
      <circle
        cx="150"
        cy="1800"
        r="25"
        stroke-width="8"
        stroke-linecap="round"
        stroke="#ffd9c4"
        fill="#ffd9c4"
      />
      <circle
        cx="575"
        cy="1950"
        r="175"
        stroke-width="8"
        stroke-linecap="round"
        stroke="#ffd9c4"
        fill="none"
        class:c-selected={selectedArea === "NO2"}
        bind:this={no2c}
      />
      <text id="no2-info" x="575" y="1900" text-anchor="middle" fill="#002987">NO2</text>
      <text class="bold" x="575" y="1970" text-anchor="middle" fill="#002987"
        >{getPrice("NO2").toFixed(2).replace(".", ",")}
      </text>
      <text x="575" y="2040" text-anchor="middle" fill="#002987">øre/kWh</text>
    </g>
  </g>
</svg>

<style lang="postcss">
  :global(.hover) {
    fill: var(--primary-strong-80);
  }
  :global(.selected) {
    fill: var(--tundra);
  }
  :global(.selected:hover) {
    fill: var(--primary-80);
  }
  :global(.c-hover) {
    fill: var(--glod-20);
  }
  :global(.c-selected) {
    fill: var(--glod-60);
  }

  /* Provide visible focus for keyboard users */
  g[role="tab"]:focus-visible,
  g[role="tab"]:focus-within {
    outline: 5px solid var(--focus-ring, var(--geitrams));
    outline-offset: 2px;
    cursor: pointer;
  }
  g[role="tab"] {
    cursor: pointer;
  }

  text {
    font-size: 4rem;
  }
  .bold {
    font-weight: bold;
  }
</style>
